/**
 * reportMarkers.js
 * Execution-state markers for the global reportData object.
 *
 * Why this exists: the export modal used to gate each report section on a payload field
 * (crop_recommendations, yield_prediction, soil_classification). Nothing ever wrote those
 * fields, so the gates were permanently false and the sections silently vanished from the PDF.
 * A module that ran and legitimately produced an empty result was also indistinguishable from
 * one that never ran at all.
 *
 * Every module now records that it ran, separately from what it produced.
 */

export const nowIso = () => new Date().toISOString();

/**
 * Record that a module ran, without writing any payload fields.
 * @param {Function} setReportData  the setState updater from App.js
 * @param {string}   moduleId       key from the frozen module_runs set
 * @param {boolean}  [ok=true]      false when the run completed but failed
 * @param {string?}  [error=null]   human-readable failure reason
 */
export function markRun(setReportData, moduleId, ok = true, error = null) {
  if (!setReportData || !moduleId) return;
  setReportData(prev => ({
    ...prev,
    module_runs: {
      ...(prev.module_runs || {}),
      [moduleId]: { ran: true, ran_at: nowIso(), ok: Boolean(ok), error: error || null },
    },
  }));
}

/**
 * Write payload fields AND the run marker in a single state update.
 * Preferred over markRun — two separate setState calls can interleave and lose one another.
 *
 * @param {Function} setReportData
 * @param {string}   moduleId
 * @param {Object}   fields         payload fields to merge into reportData
 * @param {boolean}  [ok=true]
 * @param {string?}  [error=null]
 */
export function commitModule(setReportData, moduleId, fields, ok = true, error = null) {
  if (!setReportData || !moduleId) return;
  setReportData(prev => ({
    ...prev,
    ...(fields || {}),
    module_runs: {
      ...(prev.module_runs || {}),
      [moduleId]: { ran: true, ran_at: nowIso(), ok: Boolean(ok), error: error || null },
    },
  }));
}

/** True when the named module has recorded a run. */
export function hasRun(reportData, moduleId) {
  return Boolean(reportData && reportData.module_runs && reportData.module_runs[moduleId] &&
    reportData.module_runs[moduleId].ran);
}
